import { useCallback, useEffect, useState } from "react";
import { getApi, postApi } from "../utils/api";
import {
  BvnFaceMatchPayload,
  CountriesResponse,
  FaceMatchResponse,
  FileUploadPayload,
  FileUploadResponse,
  IBankBranch,
  IBVNValidation,
  IEmploymentInfo,
  IOccupation,
  IRelationship,
  IReligion,
  ISocialMediaPlatform,
  IValidateOtpPayload,
  IValidateOtpResponse,
  LogLivenessPayload,
  IMeansOfIdentification,
  MinorDetailsType,
  MinorDocsPayload,
  RegularSavingsDocsPayload,
  RequestOtpPayload,
  SubmitSwiftDocsPayload,
  UpdateDetailsResponse,
  UploadSwiftDocsResponse,
  Icurrency,
  DomDocsUploadPayload,
  RegularSavingsDetailsPayload,
  UploadRegularDocsResponseObj,
  ValidateIdentityType,
  MinorDetailsResponse,
  DomDocUploadResponse,
  BVNNINValidationResponse,
  CountryDTO,
  AvailableChannelsResponse,
  UpdateCampaignDetailsPayload,
  YouVerifyTokenSessionRequest,
  YouVerifyTokenSessionResponse,
  HypervergeTokenResponse,
  GetFacialsChannelsResponse,
  AllAccountNumberByTINResponse,
  TINValidationResponse,
  ICurrencyAccountType,
  VerifyBusinessNumberRequest,
  VerifyBusinessNumberResponse,
  ConvertImageToBase64Response,
} from "../models/IEnums.model";
import {
  AcctLoginType,
  Source,
  SwiftSavingsDetails,
  ValidateAccWithBVNNINPayload,
  ValidateAccWithBVNNINResponse,
  ValidateBVNNINDobPayload,
} from "../types/swiftSavings";
import { errorToast, successToast } from "../utils/makeToast";
import { useDispatch } from "react-redux";
import { setAccountOpeningStep } from "../redux/accountOpening";
import { accountOpeningSteps, accountTypeMap, LS } from "../utils/constants";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "../utils/localStorage";
import useUserValidated from "./useUserValidated";
import {
  addBase64ImagePrefix,
  dateToISOString,
  generateRandomFourDigitNumber,
  getErrorMessage,
  isCampaignMode,
  normalizeKeys,
  returnConfigVariable,
  safeGet,
  sortArray,
} from "../utils/helpers";
import countries from "../utils/countries.json";
import {
  pushAccountOpened,
  pushBVNValidated,
  pushDocumentsUploaded,
} from "../utils/dataLayerEvents";
import { IAccountType } from "../models/IAccount";
import {
  AcctNoNin,
  SelectUpgradeAccountType,
  UpgradeAccountRequest,
  ValidateAccountResponse,
} from "../types/upgradeAccount";
import { useSearchParams } from "react-router-dom";

const apiHooks = {
  useGetAllCountriesAndStates: () => {
    const [allCountries, setAllCountries] = useState<CountryDTO[]>([]);

    const getCountryStates = useCallback(
      (country: string) =>
        allCountries
          ?.find(
            (item) =>
              item?.name?.toLowerCase() === country?.toLowerCase() ||
              item?.name?.toLowerCase().includes(country?.toLowerCase()),
          )
          ?.states?.map((item) => ({
            name: item.name,
            lgas: item.cities.map((city) => city.name),
          })),
      [allCountries],
    );

    useEffect(() => {
      setAllCountries((countries || []) as CountryDTO[]);
    }, [countries]);
    return { allCountries, getCountryStates };
  },

  useUserDetails: <T = Record<string, any>>() => {
    const [userDetails, setUserDetails] = useState<T>(
      () => getLocalStorageItem("userDetails") as T,
    );
    const handleUpdateUserDetails = <O extends object>(obj: O) => {
      const updatedDetails = { ...userDetails, ...obj };
      setUserDetails(updatedDetails);
      setLocalStorageItem("userDetails", JSON.stringify(updatedDetails));
    };

    const clearUserDetails = () => setUserDetails(null);
    useEffect(() => {
      const detailsFromStorage = getLocalStorageItem("userDetails");
      detailsFromStorage && setUserDetails(detailsFromStorage);
    }, []);

    return { userDetails, handleUpdateUserDetails, clearUserDetails };
  },

  useGetEmploymentInfo: () => {
    const [employmentInfo, setEmploymentInfo] = useState<IEmploymentInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const getSelectedEmployment = (code: number) => {
      return employmentInfo.find((item) => item.id === code)?.name || "";
    };
    useEffect(() => {
      getApi<IEmploymentInfo[]>(`Enum/EmployementInfo`)
        .then((response) => setEmploymentInfo(response.data || []))
        .catch((error) => console.error("An error occured"))
        .finally(() => setLoading(false));
    }, []);
    return {
      employmentInfo,
      getSelectedEmployment,
      gettingEmploymentInfo: loading,
    };
  },

  useGetCountries: () => {
    const [countries, setCountries] = useState<CountriesResponse[]>([]);

    const nigeriaObj = (countries || [])?.find(
      (item) => item?.lonG_DESC_ENG?.toLowerCase() === "nigeria",
    );
    const otherCountries = (countries || [])?.filter(
      (item) => item?.lonG_DESC_ENG?.toLowerCase() !== "nigeria",
    );
    const newCountriesArr = [nigeriaObj, ...otherCountries];
    const getSelectedCountry = (code: string) => {
      if (!code) return "";
      return (
        (newCountriesArr || [])?.find((item) => item?.countrY_CODE === code)
          ?.lonG_DESC_ENG || ""
      );
    };
    useEffect(() => {
      getApi<CountriesResponse[]>(`Enum/Country`)
        .then((response) =>
          setCountries(sortArray(response.data, "lonG_DESC_ENG")),
        )
        .catch((error) => console.error({ error }));
    }, []);
    return { newCountriesArr, getSelectedCountry };
  },

  useGetCurrencies: () => {
    const [currencies, setCurrencies] = useState<Icurrency[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const accountTypeFromStorage =
      localStorage.getItem("altFinanceAccountType") || "Corporate";
    const accountType = accountTypeMap[accountTypeFromStorage];

    const handleGetGlCode = (currencyCode: number): string => {
      return (
        currencies.find((item) => item.currencY_CODE === currencyCode)
          ?.gL_CODE || ""
      );
    };

    const getForeignCurrencies = () =>
      currencies.filter(
        (currency) => currency.brieF_DESC_ENG.toLowerCase() !== "ngn",
      );

    useEffect(() => {
      getApi<Icurrency[], { accountType: ICurrencyAccountType }>(
        `Enum/Currency`,
        { accountType },
      )
        .then((response) => setCurrencies(response.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, [accountType]);
    return {
      currencies,
      gettingCurrencies: loading,
      handleGetGlCode,
      getForeignCurrencies,
    };
  },

  useGetMeansOfId: () => {
    const [idTypes, setIdTypes] = useState<IMeansOfIdentification[]>([]);

    const handleClickIdType = <T>(
      text: string,
      callBack: <K extends keyof T>(name: K, value: T[K]) => void,
    ) => {
      const idTypeCode = idTypes.find((id) => id.text === text)?.value;
      callBack("identityCardType" as keyof T, +idTypeCode as T[keyof T]);
    };

    useEffect(() => {
      getApi<IMeansOfIdentification[]>(`Enum/MeansOfIdentification`)
        .then((response) => setIdTypes(response.data))
        .catch((error) => console.error("An error occured"));
    }, []);
    return { idTypes, handleClickIdType };
  },

  useGetOccupations: () => {
    const [occupations, setOccupations] = useState<IOccupation[]>([]);

    const getSelectedOccupation = (occId: IOccupation["id"]) => {
      if (!occId) return "";
      return occupations.find((item) => item.id === occId)?.name || "";
    };
    useEffect(() => {
      getApi<IOccupation[]>(`Enum/Occupation`)
        .then((response) => setOccupations(response.data))
        .catch((error) => console.error("An error occured"));
    }, []);
    return { occupations, getSelectedOccupation };
  },

  useGetRelationships: () => {
    const [relationships, setRelationships] = useState<IRelationship[]>([]);

    useEffect(() => {
      getApi<IRelationship[]>("Enum/Relationship")
        .then((response) => setRelationships(response.data))
        .catch((error) => console.error("An error occured"));
    }, []);
    return { relationships };
  },

  useGetSocialMediaTypes: () => {
    const [socialMedia, setSocialMedia] = useState<ISocialMediaPlatform[]>([]);

    const getSocialMedia = (id: ISocialMediaPlatform["id"]): string => {
      if (!id) return "";
      return socialMedia.find((item) => item?.id === id)?.name || "";
    };

    useEffect(() => {
      getApi<ISocialMediaPlatform[]>("Enum/SocialMedia")
        .then((response) => setSocialMedia(response.data || []))
        .catch((error) => console.error("An error occured"));
    }, []);
    return { socialMedia, getSocialMedia };
  },

  useGetBranchCodes: () => {
    const [branches, setBranches] = useState<IBankBranch[]>([]);

    const getSelectedBranch = (branchCode: IBankBranch["brancH_CODE"]) => {
      if (!branchCode) return "";
      return (
        branches.find((branch) => branch?.brancH_CODE === `${branchCode}`)
          ?.branch || ""
      );
    };

    useEffect(() => {
      getApi<IBankBranch[]>("Enum/BranchCodeList")
        .then((response) => setBranches(response.data || []))
        .catch((error) => console.error("An error occured"));
    }, []);
    return { branches, getSelectedBranch };
  },

  useGetReligions: () => {
    const [religions, setReligions] = useState<IReligion[]>([]);

    const getSelectedReligion = (religionId: IReligion["id"]) => {
      if (!religionId) return "";
      return (
        religions.find((religion) => religion?.id === religionId)?.religion ||
        ""
      );
    };
    useEffect(() => {
      getApi<IReligion[]>("Enum/Religion")
        .then((response) => setReligions(response.data || []))
        .catch((error) => console.error("An error occured"));
    }, []);
    return { religions, getSelectedReligion };
  },

  useGetSavingsAccountTypes: () => {
    const [savingsAccountTypes, setSavingsAccountTypes] = useState<
      IAccountType[]
    >([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      getApi<IAccountType[]>("Enum/SavingsAccountType")
        .then((res) => {
          const filteredData = res.data.filter((x: any) => !!x.glCode);
          setSavingsAccountTypes(filteredData);
        })
        .catch()
        .finally(() => setLoading(false));
    }, []);
    return { savingsAccountTypes, gettingSavingsAccountTypes: loading };
  },

  useGetCurrentAccountTypes: () => {
    const [currentAccountTypes, setCurrentAccountTypes] = useState<
      IAccountType[]
    >([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      getApi<Array<IAccountType>>("Enum/CurrentAccountType")
        .then((res) => {
          const filteredData = res.data.filter((x) => x.glCode);
          setCurrentAccountTypes(filteredData);
        })
        .catch()
        .finally(() => setLoading(false));
    }, []);

    return { currentAccountTypes, gettingCurrentAccountTypes: loading };
  },

  useValidateBVNNinDob: () => {
    const [loading, setLoading] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [apiResponse, setApiResponse] =
      useState<BVNNINValidationResponse>(null);
    const handleValidateBvnNin = ({
      payload,
      type,
      callBack,
    }: {
      payload: ValidateBVNNINDobPayload;
      type: AcctLoginType;
      callBack: () => void;
    }) => {
      setLoading(true);
      setApiResponse({} as IBVNValidation);
      setIsValidated(false);
      postApi<BVNNINValidationResponse, ValidateBVNNINDobPayload>(
        `Authentication/Validate${
          type === "bvn" ? "BvnDateOfBirth" : "NINForTierOne"
        }`,
        payload,
      )
        .then((response) => {
          const { isSuccess, responseMessage, success, message } =
            response.data;
          setApiResponse(response.data);
          setIsValidated(true);
          const handleNextStep = () => {
            errorToast(responseMessage || message);
            callBack();
          };
          return isSuccess || success
            ? successToast(responseMessage || message)
            : handleNextStep();
        })
        .catch((error) => {
          console.error({ error });

          errorToast(
            getErrorMessage(error) ||
              `An error occured while validating ${type.toUpperCase()}. Try again`,
          );
        })
        .finally(() => setLoading(false));
    };
    return {
      handleValidateBvnNin,
      isValidatingBvnDob: loading,
      bvnDobValidated: isValidated,
      apiResponse,
    };
  },

  useValidateIdentity: () => {
    const [loading, setLoading] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [apiResponse, setApiResponse] =
      useState<BVNNINValidationResponse>(null);

    const handleValidateIdentity = (
      payload: ValidateIdentityType,
      callBack?: () => void,
    ) => {
      setLoading(true);
      setIsValidated(false);
      postApi<BVNNINValidationResponse, ValidateIdentityType>(
        `Authentication/ValidateIdentity`,
        payload,
      )
        .then((response) => {
          const { isSuccess, responseMessage } = response.data;
          setApiResponse(response.data);
          setIsValidated(true);
          const handleNextStep = () => {
            errorToast(responseMessage);
            callBack();
          };
          return isSuccess ? successToast(responseMessage) : handleNextStep();
        })
        .catch(() =>
          errorToast(`An error occured while validating identity. Try again`),
        )
        .finally(() => setLoading(false));
    };
    return {
      handleValidateIdentity,
      validatingIdentity: loading,
      idValidationResponse: apiResponse,
      idValidated: isValidated,
    };
  },

  useValidateAccBvnNin: () => {
    const [loading, setLoading] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [errorSource, setErrorSource] = useState<Source>("");
    const handleValidateAcctBvnNin = <T>(
      payload: ValidateAccWithBVNNINPayload,
      callBack1: () => void,
      callBack2: () => void,
    ) => {
      setLoading(true);
      setIsValidated(false);
      postApi<ValidateAccWithBVNNINResponse, ValidateAccWithBVNNINPayload>(
        `Authentication/AllAccountNumberByBvnNIN`,
        payload,
      )
        .then((response) => {
          setIsValidated(true);
          const { source, data } = response.data;
          if (!!data.length) {
            setErrorSource(source);
            return callBack1();
          }
          callBack2();
        })
        .catch((error) => {
          console.error({ error });
          const { isSwiftAccount } = payload;
          errorToast(
            `An error occured while validating account number with ${
              isSwiftAccount ? "BVN" : "BVN and NIN"
            }. Try again`,
          );
        })
        .finally(() => setLoading(false));
    };
    return {
      handleValidateAcctBvnNin,
      isValidatingAcct: loading,
      acctIsValidated: isValidated,
      errorSource,
    };
  },

  useValidateOtp: () => {
    const dispatch = useDispatch();
    const { handleUpdateUserDetails, userDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);
    const [otpDetails, setOtpDetails] = useState<IValidateOtpResponse>(
      {} as IValidateOtpResponse,
    );
    const accountType = localStorage.getItem("altFinanceAccountType");

    const goToNextStep = (
      validationType: "bvn" | "otp" = "otp",
      url: string = "",
    ) => {
      localStorage.setItem("validationType", validationType);
      !!url && localStorage.setItem("userImage", url);
      pushBVNValidated();
      dispatch(
        setAccountOpeningStep(
          accountOpeningSteps[
            accountType?.includes("Minor") ? "guardianDetails" : "updateDetail"
          ],
        ),
      );
    };
    const handleValidateOtp = (
      payload: IValidateOtpPayload,
      callBack?: () => void,
    ) => {
      setLoading(true);
      postApi<IValidateOtpResponse, IValidateOtpPayload>(
        `Authentication/validateOTP`,
        payload,
      )
        .then((response) => {
          const apiResponse = response.data;
          const { isSuccess, message, accessToken } = apiResponse;
          setOtpDetails(apiResponse);
          if (isSuccess || !!accessToken?.trim()) {
            successToast(message);
            handleUpdateUserDetails(apiResponse);
            if (callBack) {
              return callBack();
            }
            goToNextStep();
          } else errorToast(message);
        })
        .catch((error) => {
          console.error({ error });
          errorToast(getErrorMessage(error));
        })
        .finally(() => setLoading(false));
    };
    return {
      handleValidateOtp,
      goToNextStep,
      otpDetails,
      isValidatingOtp: loading,
      userDetails,
    };
  },

  useRequestOtp: () => {
    const { handleUpdateUserDetails, userDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);

    const handleRequestOtp = (
      payload: RequestOtpPayload,
      callBack: () => void,
    ) => {
      setLoading(true);
      postApi<FileUploadResponse, RequestOtpPayload>(
        `Authentication/SendOTP`,
        payload,
      )
        .then((response) => {
          const otpResponse = response.data;

          const { isSuccess, message, id: otp, otpExpiry } = otpResponse || {};
          if (isSuccess && !!otp) {
            successToast(message);
            handleUpdateUserDetails({ otp, otpExpiry });
            callBack();
          } else errorToast(message);
        })
        .catch((error) => {
          console.error({ error });
          errorToast(`An error occured while resending OTP. Try again`);
        })
        .finally(() => setLoading(false));
    };
    return {
      handleRequestOtp,
      requestingOtp: loading,
      userDetails,
    };
  },
  useRequestVoiceWhatsappOtp: () => {
    const { handleUpdateUserDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);
    const [successful, setSuccessful] = useState(false);

    const handleRequestOtp = (
      payload: RequestOtpPayload,
      callBack: () => void,
    ) => {
      setLoading(true);
      postApi<FileUploadResponse, RequestOtpPayload>(
        `Authentication/SendVoiceWhatsapOtp`,
        payload,
      )
        .then((response) => {
          const otpResponse = response?.data;
          const { isSuccess, message, id: otp } = otpResponse || {};
          if (isSuccess) {
            setSuccessful(isSuccess);
            successToast(message);
            handleUpdateUserDetails({ otp });
            callBack();
          } else message && errorToast(message);
        })
        .catch((error) => {
          console.error({ error });
          errorToast(`An error occured while resending OTP. Try again`);
        })
        .finally(() => setLoading(false));
    };
    return {
      handleRequestOtp,
      requestingOtp: loading,
      successful,
      setSuccessful,
    };
  },

  usePostSwiftDetails: () => {
    const dispatch = useDispatch();
    const { handleUpdateUserDetails, userDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);

    const handleSubmitSwiftDetails = (
      payload: SwiftSavingsDetails,
      callBack?: () => void,
    ) => {
      setLoading(true);
      postApi<UpdateDetailsResponse, SwiftSavingsDetails>(
        `SwiftSavings/Details`,
        payload,
      )
        .then((response) => {
          const { isSuccess, message, obj } = response?.data;
          const { bvn, ...rest } = obj || {};
          if (isSuccess) {
            successToast(message);
            dispatch(setAccountOpeningStep(accountOpeningSteps.documentUpload));
            handleUpdateUserDetails(rest);
            callBack && callBack();
          } else errorToast(message);
        })
        .catch((error) => {
          console.error({ error });
          errorToast(`An error occured while submitting details`);
        })
        .finally(() => setLoading(false));
    };

    return {
      handleSubmitSwiftDetails,
      postingSwiftDetails: loading,
      userDetails,
    };
  },

  useUploadSwiftDocs: () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);

    const handleUploadSwiftDocs = (payload: SubmitSwiftDocsPayload) => {
      setLoading(true);
      postApi<UploadSwiftDocsResponse, SubmitSwiftDocsPayload>(
        `SwiftSavings/documentupload`,
        payload,
      )
        .then((response) => {
          const { isSuccess, message, obj } = response.data;
          if (isSuccess) {
            pushDocumentsUploaded("personal");
            const { accountNo } = (obj || {}) as UploadRegularDocsResponseObj;
            localStorage.setItem("generatedAccountNumber", accountNo);
            dispatch(
              setAccountOpeningStep(accountOpeningSteps.accountOpeningSuccess),
            );
            successToast(message);
          } else
            errorToast(message || "An error occured while uploading documents");
        })
        .catch((error) => {
          console.error("An error occured");
          errorToast(getErrorMessage(error));
        })
        .finally(() => setLoading(false));
    };
    return { handleUploadSwiftDocs, uploadingDocs: loading };
  },

  useUploadFile: () => {
    const { userDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);
    const handleUploadFile = (
      payload: FileUploadPayload,
      callBack: (url: string) => void,
    ) => {
      const modifiedPayload: FileUploadPayload = {
        ...payload,
        filetype: payload.filetype.split("/")[1],
        documentName: `${userDetails.firstName || ""} ${
          userDetails?.middleName || ""
        } ${
          userDetails?.lastName || ""
        }_${generateRandomFourDigitNumber()}`.trim(),
      };
      setLoading(true);
      postApi<FileUploadResponse, FileUploadPayload>(
        `Authentication/UploadFile`,
        modifiedPayload,
      )
        .then((response) => {
          const { id, isSuccess, message } = response.data;
          if (isSuccess) {
            successToast(message || "Uploaded successfully!");
            callBack(id);
          } else errorToast(message || "An error occured while uploading file");
        })
        .catch((err) => {
          errorToast(getErrorMessage(err));
          callBack("");
        })
        .finally(() => setLoading(false));
    };
    return { uploadingFile: loading, handleUploadFile };
  },

  useValidateNIN: () => {
    const [loading, setLoading] = useState(false);

    const handleValidateNin = (payload) => {};
  },

  useLogLiveness: () => {
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<FileUploadResponse>(null);
    const handleLogLiveness = (
      payload: LogLivenessPayload,
      callBack: () => void,
    ) => {
      setLoading(true);
      postApi<FileUploadResponse, LogLivenessPayload>(
        `Authentication/LogLiveness`,
        payload,
      )
        .then((response) => {
          const responseData = response.data;
          setApiResponse(responseData);
          const { isSuccess, message, id } = responseData;
          if (isSuccess) {
            callBack();
            successToast(
              `Liveness check ${
                payload.isSuccess ? "passed" : "failed"
              }. ${message}`,
            );
          } else errorToast(message);
        })
        .catch(() => errorToast(`An error occured`))
        .finally(() => setLoading(false));
    };
    return {
      loggingLiveness: loading,
      setLoading,
      handleLogLiveness,
      apiResponse,
    };
  },

  useBVNFaceMatch: () => {
    const { handleIsValidated } = useUserValidated();
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] =
      useState<FaceMatchResponse<any, any>>(null);

    const handleFaceMatch = (
      payload: BvnFaceMatchPayload,
      callBack?: () => void,
      onError?: () => void,
    ) => {
      setLoading(true);
      setApiResponse(null);
      postApi<FaceMatchResponse<any, any>, BvnFaceMatchPayload>(
        `Authentication/ValidateBVNFaceMatch`,
        payload,
      )
        .then((response) => {
          setApiResponse(response.data);
          const { isSuccess, responseMessage } = response.data;
          handleIsValidated(isSuccess);
          if (isSuccess) {
            successToast(responseMessage);
            callBack && callBack();
          } else errorToast(responseMessage);
        })
        .catch(() => {
          handleIsValidated(false);
        })
        .finally(() => {
          onError && onError();
          setLoading(false);
        });
    };
    return { handleFaceMatch, matchingFace: loading, apiResponse };
  },

  /**REGULAR SAVINGS */
  useSubmitSavingsDetails: () => {
    const dispatch = useDispatch();
    const { handleUpdateUserDetails, userDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<any>(null);
    const handlePostRegularDetails = (
      payload: RegularSavingsDetailsPayload,
    ) => {
      setLoading(true);
      postApi<FileUploadResponse, RegularSavingsDetailsPayload>(
        `SavingsAccount/details`,
        payload,
      )
        .then((response) => {
          const { isSuccess, message = "", obj = {} } = response.data;
          if (isSuccess) {
            const { bvn, ...rest } = obj;
            successToast(message);
            dispatch(setAccountOpeningStep(accountOpeningSteps.documentUpload));
            handleUpdateUserDetails({
              ...rest,
              ...payload,
              email: payload.emailAddress,
            });
          } else errorToast(message);
        })
        .catch((error) => console.error({ error }))
        .finally(() => setLoading(false));
    };
    return {
      handlePostRegularDetails,
      submittingRegularDetails: loading,
      userDetails,
    };
  },

  useUploadSavingsDocs: () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] =
      useState<UploadRegularDocsResponseObj>(null);

    const handleUploadRegularDocs = (payload: RegularSavingsDocsPayload) => {
      setLoading(true);
      postApi<FileUploadResponse, RegularSavingsDocsPayload>(
        `SavingsAccount/documentupload`,
        payload,
      )
        .then((response) => {
          const responseData = response.data;
          const { isSuccess, message, obj } = responseData || {};
          const { accountNo } = (obj || {}) as UploadRegularDocsResponseObj;
          if (isSuccess) {
            pushDocumentsUploaded("personal");
            successToast(message);
            setApiResponse(obj);
            localStorage.setItem("generatedAccountNumber", accountNo);
            dispatch(
              setAccountOpeningStep(accountOpeningSteps.accountOpeningSuccess),
            );
          } else errorToast(message || "Unable to upload documents");
        })
        .catch((error) => {
          console.error({ error });
          errorToast(`An error occured while uploading documents`);
        })
        .finally(() => setLoading(false));
    };
    return {
      handleUploadRegularDocs,
      submittingRegularDocs: loading,
      regularDocsUploadResponse: apiResponse,
    };
  },

  /**MINOR SAVINGS */
  useUpdateMinorDetails: () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<any>(null);
    const { handleUpdateUserDetails, userDetails } = apiHooks.useUserDetails();
    const handleUpdateMinorDetails = (
      payload: MinorDetailsType,
      callBack?: () => void,
    ) => {
      setLoading(true);
      postApi<MinorDetailsResponse, MinorDetailsType>(
        `MinorSavings/Details`,
        payload,
      )
        .then((response) => {
          const { isSuccess, obj, message } = response.data;
          if (isSuccess) {
            const { bvn, ...rest } = obj || {};
            successToast(message);
            handleUpdateUserDetails(rest);
            dispatch(setAccountOpeningStep(accountOpeningSteps.documentUpload));
          } else errorToast(message);
        })
        .catch((error) => console.error({ error }))
        .finally(() => setLoading(false));
    };
    return {
      updatingMinorDetails: loading,
      handleUpdateMinorDetails,
      minorDetailsUploadResponse: apiResponse,
      userDetails,
    };
  },

  useUploadMinorDocs: () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const handleUploadMinorDocs = (payload: MinorDocsPayload) => {
      setLoading(true);
      postApi<FileUploadResponse, MinorDocsPayload>(
        `MinorSavings/documentupload`,
        payload,
      )
        .then((response) => {
          const { isSuccess, message, obj } = response?.data;
          if (isSuccess) {
            pushDocumentsUploaded("personal");
            const { accountNo } = (obj || {}) as UploadRegularDocsResponseObj;
            localStorage.setItem("generatedAccountNumber", accountNo);
            dispatch(
              setAccountOpeningStep(accountOpeningSteps.accountOpeningSuccess),
            );
          } else
            errorToast(
              message ||
                "An error occured while uploading documents. Try again.",
            );
        })
        .catch((error) => console.error({ error }))
        .finally(() => setLoading(false));
    };

    return { handleUploadMinorDocs, uploadingMinorDocs: loading };
  },

  /**DOMICILIARY SAVINGS */
  useUploadDomDocs: () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const handleUploadDomDocs = (payload: DomDocsUploadPayload) => {
      setLoading(true);
      postApi<DomDocUploadResponse, DomDocsUploadPayload>(
        `DomiciliarySavings/documentupload`,
        payload,
      )
        .then((response) => {
          const { isSuccess, message, id, obj } = response?.data;
          if (isSuccess) {
            pushDocumentsUploaded("personal");
            const { accountNo } = (
              (obj || []) as UploadRegularDocsResponseObj[]
            )[0];
            localStorage.setItem("generatedAccountNumber", accountNo);
            successToast(message);
            dispatch(
              setAccountOpeningStep(accountOpeningSteps.accountOpeningSuccess),
            );
          } else errorToast(message);
        })
        .catch((error) => console.error({ error }))
        .finally(() => setLoading(false));
    };

    return { handleUploadDomDocs, uploadingDomDocs: loading };
  },

  useUploadDomDetails: () => {
    const dispatch = useDispatch();
    const { handleUpdateUserDetails, userDetails } = apiHooks.useUserDetails();
    const [loading, setLoading] = useState(false);

    const handleSubmitDomDetails = (
      payload: SwiftSavingsDetails,
      callBack?: () => void,
    ) => {
      setLoading(true);
      postApi<UpdateDetailsResponse, SwiftSavingsDetails>(
        `DomiciliarySavings/Details`,
        payload,
      )
        .then((response) => {
          const { isSuccess, message, obj } = response.data;
          if (isSuccess) {
            const { bvn, ...rest } = obj || {};
            successToast(message);
            dispatch(setAccountOpeningStep(accountOpeningSteps.documentUpload));
            handleUpdateUserDetails({
              ...payload,
              ...rest,
              email: payload.emailAddress,
            });
            callBack && callBack();
          } else errorToast(message);
        })
        .catch((error) => {
          console.error("An error occured");
          errorToast(`An error occured while submitting details`);
        })
        .finally(() => setLoading(false));
    };

    return {
      handleSubmitDomDetails,
      postingDomDetails: loading,
      userDetails,
    };
  },
  useGetAvailableChannels: () => {
    const [loading, setLoading] = useState(true);
    const [response, setResponse] = useState<AvailableChannelsResponse>(
      {} as AvailableChannelsResponse,
    );
    const [searchParams] = useSearchParams();
    const tierType: SelectUpgradeAccountType["type"] = (searchParams.get(
      "type",
    ) || "") as SelectUpgradeAccountType["type"];

    const getAvailableChannels = () => {
      getApi<AvailableChannelsResponse>(`Authentication/GetAvailableChannels`)
        .then((response) => {
          const { availableOtps = [] } = response.data.data;
          if (tierType === "tier2") {
            const modifiedArr = [...availableOtps].filter(
              (item) => item !== "BvnFaceMatch",
            );
            const updatedResponse: AvailableChannelsResponse = {
              ...response.data,
              data: {
                availableOtps: modifiedArr,
              },
            };
            return setResponse(updatedResponse);
          }
          setResponse(response.data);
        })
        .catch(() => {
          errorToast(`An error occured while fetching available channels`);
        })
        .finally(() => setLoading(false));
    };

    useEffect(() => {
      getAvailableChannels();
    }, []);
    return { response, loading };
  },
  useUpdateCampaignDetails() {
    const [loading, setLoading] = useState(true);
    const accountNumber = localStorage.getItem("generatedAccountNumber");
    const handleUpdateCampaignDetails = (
      payload: UpdateCampaignDetailsPayload,
    ) => {
      postApi<FileUploadResponse, UpdateCampaignDetailsPayload>(
        "SwiftSavings/UpdateCampaignDetails",
        payload,
      )
        .then()
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    };
    useEffect(() => {
      if (accountNumber) {
        pushAccountOpened({
          account_type: "personal",
          account_value: accountNumber,
        });
        if (isCampaignMode().isCampaignMode) {
          handleUpdateCampaignDetails({
            accountNumber,
            acquisitionAttribId: safeGet(LS.ATTR_ID),
            acquisitionChannel: "online_account",
            utmSource: safeGet(LS.UTM_SOURCE),
            utmMedium: safeGet(LS.UTM_MEDIUM),
            utmCampaign: safeGet(LS.UTM_CAMPAIGN),
            utmTerm: safeGet(LS.UTM_TERM),
            gclid: safeGet(LS.GCLID),
            fbclid: safeGet(LS.FBC),
            ttclid: safeGet(LS.TTCLID),
            twclid: safeGet(LS.TWCLID),
            snapclid: safeGet(LS.SNAPCLID),
            msclkid: safeGet(LS.MS_CLK_ID),
            firstTouch: dateToISOString(safeGet(LS.FIRST_TOUCH_TS)),
          });
        } else setLoading(false);
      }
    }, [accountNumber]);
    return { loading, accountNumber };
  },

  useVerifyBusinessNumber() {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [data, setData] = useState<VerifyBusinessNumberResponse | null>(null);

    const handleVerifyBusinessNumber = (
      payload: VerifyBusinessNumberRequest,
      options?: {
        onSuccess?: (data: VerifyBusinessNumberResponse) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      setLoading(true);
      postApi<VerifyBusinessNumberResponse, VerifyBusinessNumberRequest>(
        `BusinessAccount/verifyBusinessNumber`,
        payload,
      )
        .then((res) => {
          const { onSuccess } = options;
          const {
            success,
            message,
            accessToken,
            refreshToken,
            refreshTokenExpirationTime,
            accessTokenExpirationTime,
          } = res.data;
          if (success) {
            accessToken && setLocalStorageItem("accessToken", accessToken);
            setLocalStorageItem(
              "jinx",
              JSON.stringify({
                refreshToken,
                accessToken,
                accessTokenExpirationTime,
                refreshTokenExpirationTime,
              }),
            );
            setData(res.data);
            onSuccess?.(res.data);
            successToast(message);
            return;
          }
          throw new Error(message);
        })
        .catch((error) => {
          const errorMsg = getErrorMessage(error);
          errorToast(errorMsg);
          setError(errorMsg);
          const { onError } = options;
          onError?.(error);
        })
        .finally(() => setLoading(false));
    };
    return {
      handleVerifyBusinessNumber,
      isVerifyingBusinessNumber: loading,
      data,
      error,
      setError,
      setData,
    };
  },

  useGetAllAccountNumberByTIN() {
    const [loading, setLoading] = useState<boolean>(false);
    const [hasAccount, setHasAccount] = useState<boolean>(false);
    const [isTINError, setIsTINError] = useState<boolean>(false);
    const handleGetAllAccountNumberByTIN = (
      payload: { tin: string },
      callBack: () => void,
    ) => {
      setLoading(true);
      setIsTINError(false);
      postApi<AllAccountNumberByTINResponse, typeof payload>(
        "Authentication/AllAccountNumberByTIN",
        payload,
      )
        .then((res) => {
          const {
            hasAccount: tinHasAcct,
            // token,
            isSuccess,
            responseMessage,
          } = res.data;
          setHasAccount(tinHasAcct);
          // token && setLocalStorageItem("accessToken", token);
          (isSuccess ? successToast : errorToast)(responseMessage);
          const isEmpty = isSuccess && !tinHasAcct;
          if (isEmpty) {
            callBack();
          }
        })
        .catch(() => setIsTINError(true))
        .finally(() => setLoading(false));
    };
    return {
      handleGetAllAccountNumberByTIN,
      loading,
      hasAccount,
      isTINError,
      setIsTINError,
    };
  },

  useGetExistingBusinessDetails() {
    const [loading, setLoading] = useState<boolean>(false);

    const handleGetExistingDetails = (Tin: string) => {
      setLoading(true);
      getApi(`BusinessAccount/BusinessExistingDetails`, { Tin })
        .then((res) => {})
        .catch((error) => {})
        .finally(() => setLoading(false));
    };
    return { gettingExistingDetails: loading, handleGetExistingDetails };
  },

  useGetYouverifyTokenAndSessionId() {
    const [loading, setLoading] = useState<boolean>(false);
    const [authVars, setAuthVars] = useState<{
      token: string;
      sessionId: string;
    }>({
      token: "",
      sessionId: "",
    });

    const handleGetTokenAndSessionId = (
      callback: (token: string, sessionId: string) => void,
    ) => {
      setLoading(true);
      const publicMerchantID = returnConfigVariable(
        "REACT_APP_YOUVERIFY_PUBLIC_MERCHANT_KEY",
      );
      postApi<YouVerifyTokenSessionResponse, YouVerifyTokenSessionRequest>(
        "Authentication/GenerateYouVerifyTokenSession",
        {
          publicMerchantID,
        },
      )
        .then((response) => {
          const {
            isSuccess,
            authTokenResponse,
            sessionResponse,
            responseMessage,
          } = response.data;
          if (isSuccess) {
            const { authToken: token } = authTokenResponse.data;
            const { sessionId } = sessionResponse.data;
            setAuthVars((prev) => ({ ...prev, token, sessionId }));
            callback(token, sessionId);
          } else errorToast(responseMessage);
        })
        .catch(() =>
          errorToast("An error occured while getting token and session ID"),
        )
        .finally(() => setLoading(false));
    };
    return {
      handleGetTokenAndSessionId,
      gettingTokenAndSession: loading,
      authVars,
    };
  },
  useGetHypervergeToken() {
    const [loading, setLoading] = useState<boolean>(false);

    const handleGetHypervergeToken = (
      callBack: (authToken: string) => void,
    ) => {
      setLoading(true);
      getApi<HypervergeTokenResponse>("Authentication/GenerateHypervergeToken")
        .then((res) => {
          const { data } = res.data;
          const { result, statusCode } = data;
          if (statusCode !== 200) {
            return errorToast("An error occured while getting token");
          }
          const { authToken } = result;
          callBack(authToken);
        })
        .catch(() => errorToast("An error occured while getting token"))
        .finally(() => setLoading(false));
    };
    return { handleGetHypervergeToken, gettingToken: loading };
  },

  useGetAvailableSdk() {
    const [loading, setLoading] = useState<boolean>(false);

    const handleGetAvailableSdk = (
      callBack: (
        options: GetFacialsChannelsResponse["data"]["availableFacials"],
      ) => void,
    ) => {
      setLoading(true);
      getApi<GetFacialsChannelsResponse>("Authentication/GetFacialsChannels")
        .then((res) => {
          callBack(res.data.data.availableFacials);
        })
        .catch((error) => console.error({ error }))
        .finally(() => setLoading(false));
    };
    return { handleGetAvailableSdk, gettingAvailableSdk: loading };
  },

  useValidateTin() {
    const [loading, setLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);

    const handleValidateTin = (
      payload: { tin: string },
      callBack: (res: TINValidationResponse) => void,
    ) => {
      postApi<TINValidationResponse, typeof payload>(
        "BusinessAccount/TinValidation",
        payload,
      )
        .then((res) => {
          callBack(normalizeKeys<TINValidationResponse>(res.data));
        })
        .catch((error) => {
          console.error({ error });
          setIsError(true);
        })
        .finally(() => setLoading(false));
    };
    return {
      validatingTin: loading,
      handleValidateTin,
      isTinValidationError: isError,
      setValidatingTin: setLoading,
      setIsTinValidationError: setIsError,
    };
  },

  useUpgradeAccountValidation() {
    const [loading, setLoading] = useState<boolean>(false);
    const [isValidAcc, setIsValidAcc] = useState<boolean>(false);
    const { handleUpdateUserDetails } = apiHooks.useUserDetails();

    const handleValidateAccount = (
      payload: AcctNoNin,
      callBack?: () => void,
    ) => {
      setLoading(true);
      setIsValidAcc(false);
      postApi<ValidateAccountResponse, AcctNoNin>(
        `Upgrade/AccountValidation`,
        payload,
      )
        .then((res) => {
          const { isSuccess, responseMessage, ...rest } = res.data;
          if (!isSuccess) return errorToast(responseMessage);
          handleUpdateUserDetails<Partial<ValidateAccountResponse>>(rest);
          setIsValidAcc(isSuccess);
          callBack?.();
        })
        .catch((error) => errorToast(getErrorMessage(error)))
        .finally(() => setLoading(false));
    };
    return {
      handleValidateAccount,
      validatingAcct: loading,
      isValidAcc,
      setIsValidAcc,
    };
  },

  useUpgradeAccount() {
    const [loading, setLoading] = useState<boolean>(false);

    const handleUpgradeAccount = (
      payload: UpgradeAccountRequest,
      callBack: () => void,
    ) => {
      setLoading(true);
      postApi<
        Pick<IBVNValidation, "isSuccess" | "responseMessage">,
        UpgradeAccountRequest
      >(`Upgrade/UpgradeAccount`, payload)
        .then((res) => {
          const { isSuccess, responseMessage } = res.data;
          if (!isSuccess) {
            return errorToast(responseMessage);
          }
          successToast(responseMessage);
          callBack?.();
        })
        .catch((err) => {
          console.error({ err });
          errorToast(getErrorMessage(err));
        })
        .finally(() => setLoading(false));
    };
    return { upgradingAccount: loading, handleUpgradeAccount };
  },

  useConvertImageUrlToBase64() {
    const [loading, setLoading] = useState<boolean>(false);

    const imageUrlToBase64 = (
      imageUrl: string,
      callback: (base64: string) => void,
    ) => {
      setLoading(true);
      getApi<
        ConvertImageToBase64Response,
        Pick<SubmitSwiftDocsPayload, "imageUrl">
      >(`Authentication/ConvertImageToBase64`, { imageUrl })
        .then((res) => {
          const { success, dataUrl, base64, message } = res.data;
          success
            ? callback(addBase64ImagePrefix(dataUrl || base64))
            : errorToast(message);
        })
        .catch((err) => {
          console.error({ err });
          errorToast(getErrorMessage(err));
        })
        .finally(() => setLoading(false));
    };
    return { gettingBase64: loading, imageUrlToBase64 };
  },
};

export default apiHooks;
